import React, { useEffect, useRef, useState } from "react";
import { SearchIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import useApi from "@/hooks/useApi";
import S from "./styles.module.scss";

interface Hit {
  id: number;
  name: string;
}

interface Results {
  clients: Hit[];
  projects: Hit[];
  leads: Hit[];
}

const GlobalSearch = () => {
  const { getRequest } = useApi();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<Results | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const run = debounce(async (q: string) => {
      if (q.trim().length < 2) {
        setResults(null);
        return;
      }
      try {
        const res = await getRequest(`/api/search?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setResults(data.response ?? null);
      } catch {
        setResults(null);
      }
    }, 350);
    run(term);
    return () => run.cancel();
  }, [term]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const total = results
    ? results.clients.length + results.projects.length + results.leads.length
    : 0;

  const close = () => {
    setOpen(false);
    setTerm("");
  };

  const group = (label: string, items: Hit[], to: (id: number) => string) =>
    items.length > 0 ? (
      <div className={S.group}>
        <span className={S.groupTitle}>{label}</span>
        {items.map((hit) => (
          <Link
            key={`${label}-${hit.id}`}
            to={to(hit.id)}
            className={S.item}
            onClick={close}
          >
            {hit.name}
          </Link>
        ))}
      </div>
    ) : null;

  return (
    <div className={S.wrapper} ref={ref}>
      <div className={S.field}>
        <SearchIcon size={16} className={S.icon} />
        <input
          type="search"
          placeholder="Buscar clientes, projetos, leads…"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          aria-label="Busca global"
        />
      </div>

      {open && term.trim().length >= 2 && (
        <div className={S.panel} aria-label="Resultados da busca">
          {total === 0 && <p className={S.empty}>Nenhum resultado.</p>}
          {results &&
            group("Clientes", results.clients, (id) => `/clientes/${id}`)}
          {results &&
            group("Projetos", results.projects, (id) => `/projetos/${id}`)}
          {results && group("Leads", results.leads, () => "/leads")}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
